import { createClient } from '@/util/supabase/component'
import { chunk } from 'lodash'
import type { UploadTypes } from '@/parsers'
import type { UploadTableName } from '../types'
import { booleanFilterTypeGuard } from '@/util/helpers'
import { type PostgrestSingleResponse } from '@supabase/supabase-js'

export interface UploadResult {
  successCount: number
  errors: string[]
}

export class UploadService {
  private readonly supabase = createClient()

  async uploadData(
    tableName: UploadTableName,
    data: UploadTypes[],
    chunkSize: number = 1000,
  ): Promise<UploadResult> {
    const chunks = chunk(data, chunkSize)
    const successResults: Array<PostgrestSingleResponse<any>> = []
    const errorResults: Array<
      PostgrestSingleResponse<any> | { data: null; error: Error }
    > = []
    await Promise.all(
      chunks.map(async (chunk) => {
        let chunkResult
        try {
          chunkResult = await this.supabase
            .from(tableName)
            .insert(chunk, { count: 'estimated' })
            .select()
        } catch (e) {
          const error = e as Error
          errorResults.push({ data: null, error })
          return
        }

        if (Boolean(chunkResult.data) && chunkResult.status === 201) {
          successResults.push(chunkResult)
        } else {
          errorResults.push(chunkResult)
        }
        return chunkResult
      }),
    )

    const successCount = successResults.reduce(
      (acc, res) => acc + (res.count ?? 0),
      0,
    )

    const errors = errorResults
      .map((res) => res.error?.message)
      .filter(booleanFilterTypeGuard)

    return {
      successCount,
      errors,
    }
  }
}
